<template>
    <TabGroup
        :selectedIndex="selectedIndex"
        @change="onChange"
        :vertical="displayAsColumn"
    >
        <div class="px-px">
            <p
                v-if="showSingleTabAsTitle && tabs.length == 1"
                class="text-lg -ml-px font-header font-medium"
                v-text="tabs[0].title ?? tabs[0].name"
            />
            <TabList
                v-else
                :class="[
                    tabs.length > 1 && !shouldFit ? minTabWidth : '',
                    {
                        'w-fit':
                            (tabs.length == 1 && fitWhenSingle) || shouldFit,
                    },
                    roundedTabs && !displayAsColumn
                        ? 'rounded-full overflow-hidden'
                        : 'rounded-sm',

                    displayAsColumn ? 'flex-col divide-y' : 'flex-row divide-x',
                    'flex ring-1 ring-tabs-border divide-tabs-border',
                ]"
            >
                <Tab
                    v-for="tab in tabs"
                    :key="tab.id"
                    as="template"
                    v-slot="{ selected }"
                    :disabled="disabled"
                >
                    <button
                        :class="[
                            tabBaseClasses,
                            { 'cursor-not-allowed opacity-70': disabled },
                            { 'whitespace-nowrap': avoidBreakingWords },
                            selected
                                ? 'bg-tabs-active text-tabs-active-text'
                                : {
                                      'text-tabs-text bg-tabs-background hover:bg-tabs-hover hover:text-tabs-hover-text':
                                          !disabled,
                                  },
                        ]"
                    >
                        {{ tab.title ?? tab.name }}
                    </button>
                </Tab>
            </TabList>
        </div>
        <TabPanels
            v-if="slotsAreUsed"
            :class="[
                showSingleTabAsTitle && tabs.length == 1
                    ? 'pt-4'
                    : contentSpacingTop,
            ]"
        >
            <ReactiveHeightTransition :enabled="enableReactiveHeightTransition">
                <!-- Fade transition enabled -->
                <TransitionGroup v-if="enableFadeTransition" name="fade">
                    <TabPanel as="div" v-for="tab in tabs" :key="tab.id">
                        <slot :name="tab.id" :tab="tab"></slot>
                    </TabPanel>
                </TransitionGroup>
                <!-- Fade transition disabled -->
                <template v-else>
                    <TabPanel as="div" v-for="tab in tabs" :key="tab.id">
                        <slot :name="tab.id" :tab="tab"></slot>
                    </TabPanel>
                </template>
            </ReactiveHeightTransition>
        </TabPanels>
    </TabGroup>
    <!--  hidden list of tabs used to measure of the tabs would overflow if full sized -->
    <div ref="tabList" class="flex flex-row h-0 overflow-hidden">
        <button
            v-for="tab in tabs"
            :class="[
                tabBaseClasses,
                { 'whitespace-nowrap': avoidBreakingWords },
            ]"
        >
            {{ tab.title ?? tab.name }}
        </button>
    </div>
</template>

<script>
/**
 * TabGroup component
 * the slots have specific names to allow for custom content in the tabs
 *
 * usage:
 *  <TabGroupComponent :tabs="groups">
 *      <template v-for="group in groups" :slot="group.id" :tab="group">
 */

import { TabGroup, TabList, Tab, TabPanels, TabPanel } from '@headlessui/vue'
import ReactiveHeightTransition from '@/Components/Transitions/ReactiveHeightTransition.vue'
import { throttle } from '@/Tools/utilities.js'

export default {
    name: 'Tabs',

    emits: ['update:selectedTab', 'tabChanged'],

    components: {
        TabGroup,
        TabList,
        Tab,
        TabPanels,
        TabPanel,
        ReactiveHeightTransition,
    },

    data() {
        return {
            localIndex: 0,
            displayAsColumn: false,
            resizeObserver: null,
        }
    },

    created() {
        this.setBaseClasses()
    },

    props: {
        tabs: {
            type: Array,
            validator: (value) => {
                return value.every((tab) => tab.id && (tab.title || tab.name))
            },
        },

        enableReactiveHeightTransition: {
            type: Boolean,
            default: false,
        },

        enableFadeTransition: {
            type: Boolean,
            default: false,
        },

        fitWhenSingle: {
            type: Boolean,
            default: false,
        },

        shouldFit: {
            type: Boolean,
            default: false,
        },

        avoidBreakingWords: {
            type: Boolean,
            default: false,
        },

        controlCurrentTab: {
            type: Boolean,
            default: false,
        },

        selectedTab: {
            type: [String, Number],
            default: null,
        },

        initialTab: {
            type: [String, Number],
            default: null,
        },

        contentSpacingTop: {
            type: String,
            default: 'pt-4',
        },

        minTabWidth: {
            type: String,
            default: 'md:min-w-textbody',
        },

        disabled: {
            type: Boolean,
            default: false,
        },
        showSingleTabAsTitle: {
            type: Boolean,
            default: true,
        },
        smallTabs: {
            type: Boolean,
            default: false,
        },
        roundedTabs: {
            type: Boolean,
            default: false,
        },
        uppercase: {
            type: Boolean,
            default: true,
        },
    },

    mounted() {
        this.setupResizeObserver()
        if (this.initialTab) {
            this.localIndex = this.tabs.findIndex(
                (tab) => tab.id === this.initialTab
            )
        }
    },

    beforeUnmount() {
        // Clean up the observer when component is destroyed
        if (this.resizeObserver) {
            this.resizeObserver.disconnect()
        }
    },

    computed: {
        selectedIndex() {
            // If the parent wants to control the current tab, return the index of the tab with the id of the selectedTab
            if (this.controlCurrentTab) {
                return this.tabs.findIndex((tab) => tab.id === this.selectedTab)
                // Otherwise the components itself returns its index
            } else {
                return this.localIndex
            }
        },

        slotsAreUsed() {
            return this.tabs.some((tab) => this.$slots[tab.id])
        },
    },

    methods: {
        setupResizeObserver() {
            this.debouncedHandleResize = throttle(() => {
                this.handleResize()
            }, 200)
            this.resizeObserver = new ResizeObserver(this.debouncedHandleResize)
            this.resizeObserver.observe(this.$refs.tabList)
        },

        handleResize() {
            const tabListElement = this.$refs.tabList
            if (tabListElement.scrollWidth > tabListElement.clientWidth) {
                this.displayAsColumn = true
            } else {
                this.displayAsColumn = false
            }
        },

        /**
         * Checks if the tab list is overflowing
         * If it is, the tabs will be displayed as a column
         */
        isTabListOverflowing() {
            const tabListElement = this.$refs.tabList.$el
            return tabListElement.scrollWidth > tabListElement.clientWidth
        },

        onChange(index) {
            if (this.controlCurrentTab) {
                this.$emit('update:selectedTab', this.tabs[index].id)
            } else {
                this.$emit('tabChanged', {
                    from: this.localIndex,
                    to: index,
                    from_id: this.tabs[this.localIndex].id,
                    to_id: this.tabs[index].id,
                })
                this.localIndex = index
            }
        },

        setBaseClasses() {
            this.tabBaseClasses = [
                'flex-1 text-center transition duration-button-hover focus-style text-sm',
            ]

            if (this.uppercase) {
                this.tabBaseClasses.push('uppercase tracking-attribute')
            }

            if (this.smallTabs) {
                this.tabBaseClasses.push('py-0.5 px-3')
            } else {
                this.tabBaseClasses.push('py-2 px-3')
            }
        },
    },
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease;
}
</style>
